<template>
  <v-container class="pa-6">
    <!-- KPI Cards Section -->
    <v-row class="mb-6">
      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card elevation-0" outlined>
          <v-card-text class="pa-5">
            <div class="kpi-icon">
              <v-icon color="primary">mdi-database</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Total Configuraciones</div>
              <div class="kpi-value">{{ totalConfiguraciones }}</div>
              <div class="kpi-subtitle">
                <v-icon x-small color="success">mdi-check-circle</v-icon>
                {{ tiposTabla }} tipos diferentes
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card elevation-0" outlined>
          <v-card-text class="pa-5">
            <div class="kpi-icon">
              <v-icon color="success">mdi-tag-multiple</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Categorías</div>
              <div class="kpi-value">{{ totalCategoriasActivas }}</div>
              <div class="kpi-subtitle">
                <v-icon x-small color="info">mdi-point-of-sale</v-icon>
                {{ tiposPos }} puntos de venta
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card elevation-0" outlined>
          <v-card-text class="pa-5">
            <div class="kpi-icon">
              <v-icon color="info">mdi-star</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Tipo Principal</div>
              <div class="kpi-value text-truncate">{{ tipoPrincipal }}</div>
              <div class="kpi-subtitle">
                <v-icon x-small color="purple">mdi-counter</v-icon>
                {{ configuracionesPrincipales }} registros
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card elevation-0" outlined>
          <v-card-text class="pa-5">
            <div class="kpi-icon">
              <v-icon color="purple">mdi-chart-donut</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Completitud</div>
              <div class="kpi-value">{{ completitudPercentage }}%</div>
              <div class="kpi-subtitle">
                <v-icon x-small color="warning">mdi-alert</v-icon>
                {{ sinValor }} sin valor
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Main Content Card -->
    <v-card class="main-card elevation-20" outlined>
      <!-- Header with Actions -->
      <v-card-title class="pa-0">
        <v-toolbar flat class="minimal-toolbar">
          <h2 class="section-title">Configuración del Sistema</h2>
          <v-spacer></v-spacer>

          <!-- Search and Filters -->
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Buscar..."
            single-line
            hide-details
            dense
            outlined
            clearable
            class="mr-4 search-field"
            style="max-width: 250px"
          ></v-text-field>

          <v-select
            v-model="filterTabla"
            :items="tablaOptions"
            label="Tipo"
            clearable
            dense
            outlined
            hide-details
            class="mr-4"
          ></v-select>

          <v-btn color="primary" @click="newForm">
            <v-icon left>mdi-plus</v-icon>
            Nueva
          </v-btn>

          <v-btn icon class="ml-2" @click="exportToExcel" title="Exportar a Excel">
            <v-icon>mdi-file-excel</v-icon>
          </v-btn>
        </v-toolbar>
      </v-card-title>

      <!-- Data Table -->
      <v-data-table
        :headers="headers"
        :items="filteredConfiguraciones"
        :items-per-page="10"
        :search="search"
        item-key="id"
        sort-by="tabla"
        :loading="loading_status"
        loader-height="3"
        loading-text="Cargando configuraciones..."
        class="minimal-table clickable-rows"
        @click:row="editItem"
        :footer-props="{
          'items-per-page-options': [10, 25, 50, 100],
        }"
      >
        <!-- Tabla Type -->
        <template v-slot:[`item.tabla`]="{ item }">
          <v-chip
            small
            :color="getTablaColor(item.tabla)"
            :text-color="getTablaTextColor(item.tabla)"
            label
          >
            <v-icon x-small left>{{ getTablaIcon(item.tabla) }}</v-icon>
            {{ item.tabla }}
          </v-chip>
        </template>

        <!-- Código -->
        <template v-slot:[`item.codigo`]="{ item }">
          <div class="code-info">
            <span class="font-weight-medium">{{ item.codigo }}</span>
            <div v-if="item.codigo2" class="text-caption grey--text">
              Sec: {{ item.codigo2 }}
            </div>
          </div>
        </template>

        <!-- Valor -->
        <template v-slot:[`item.valor`]="{ item }">
          <div class="value-info">
            <span v-if="item.valor" class="font-weight-medium">{{ item.valor }}</span>
            <span v-else class="grey--text">
              <v-icon x-small>mdi-minus</v-icon>
              Sin valor
            </span>
          </div>
        </template>

        <!-- No data -->
        <template v-slot:no-data>
          <div class="text-center py-12">
            <v-icon size="64" color="grey lighten-2">mdi-database-off</v-icon>
            <h3 class="text-h6 mt-4 grey--text">No hay configuraciones registradas</h3>
            <v-btn color="primary" class="mt-4" @click="newForm">
              <v-icon left>mdi-plus</v-icon>
              Crear Primera Configuración
            </v-btn>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Edit/New Dialog -->
    <v-dialog v-model="dialog" max-width="600px" scrollable persistent>
      <v-card class="elevation-0">
        <v-toolbar flat class="minimal-toolbar">
          <h3 class="dialog-title">
            <v-icon small class="mr-2">mdi-database</v-icon>
            {{ editingItem ? "Editar" : "Nueva" }} Configuración
          </h3>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-form ref="form" v-model="valid" lazy-validation>
          <v-card-text class="pa-6">
            <v-row>
              <v-col cols="12" md="12">
                <v-select
                  v-model="configuracion.tabla"
                  :items="tablaTypeOptions"
                  label="Tipo de Configuración"
                  :rules="f_required"
                  :disabled="editingItem"
                  outlined
                  dense
                  prepend-inner-icon="mdi-table"
                  hide-details="auto"
                ></v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="configuracion.codigo"
                  label="Código Principal"
                  :rules="f_required"
                  :disabled="editingItem"
                  outlined
                  dense
                  prepend-inner-icon="mdi-key"
                  hide-details="auto"
                  @input="upperCase"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="configuracion.codigo2"
                  :label="
                    configuracion.tabla === 'CATEGORIA' ? 'IVA %' : 'Código Secundario'
                  "
                  :placeholder="
                    configuracion.tabla === 'CATEGORIA' ? 'Ej: 19' : 'Opcional'
                  "
                  outlined
                  dense
                  prepend-inner-icon="mdi-key-variant"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="configuracion.valor"
                  label="Valor / Descripción"
                  :rules="f_required"
                  outlined
                  dense
                  prepend-inner-icon="mdi-text"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
            </v-row>

            <!-- Información adicional -->
            <v-alert type="info" dense text class="mt-4" v-if="!editingItem">
              <v-icon small class="mr-2">mdi-information</v-icon>
              El código principal no podrá ser modificado después de crear el registro
            </v-alert>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions class="pa-6">
            <v-spacer></v-spacer>
            <v-btn text @click="closeDialog"> Cancelar </v-btn>
            <v-btn
              color="primary"
              @click="saveConfiguracion"
              :loading="saving"
              :disabled="!valid"
            >
              <v-icon left>mdi-content-save</v-icon>
              {{ editingItem ? "Actualizar" : "Guardar" }}
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400px" persistent>
      <v-card class="elevation-0">
        <v-card-title class="text-h6">
          <v-icon color="warning" class="mr-2">mdi-alert</v-icon>
          Confirmar Eliminación
        </v-card-title>
        <v-card-text class="pt-4">
          ¿Está seguro que desea eliminar esta configuración?
          <v-alert type="warning" dense outlined class="mt-3">
            <div class="font-weight-medium">
              {{ selectedForDelete && selectedForDelete.tabla }}
            </div>
            <div class="text-caption">
              Código: {{ selectedForDelete && selectedForDelete.codigo }}
            </div>
          </v-alert>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text @click="cancelDelete"> Cancelar </v-btn>
          <v-btn color="error" @click="deleteItem" :loading="deleting">
            <v-icon left>mdi-delete</v-icon>
            Eliminar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar para mensajes -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      top
    >
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false"> Cerrar </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { webserver } from "@/services/webserver.js";

export default {
  name: "Settings",
  data() {
    return {
      // Snackbar
      snackbar: {
        show: false,
        text: "",
        color: "info",
        timeout: 3000,
      },

      // Form validation
      valid: false,
      f_required: [(v) => !!v || "Campo requerido"],

      // UI State
      saving: false,
      deleting: false,
      loading_status: false,
      dialog: false,
      deleteDialog: false,
      editingItem: false,

      // Search and filters
      search: "",
      filterTabla: null,
      filterIdPos: null,

      // Data
      tabla_lst: [],
      configuracion: {},
      selectedForDelete: null,

      // Table headers
      headers: [
        {
          text: "Tipo",
          align: "start",
          sortable: true,
          value: "tabla",
          width: "25%",
        },
        {
          text: "Código",
          align: "start",
          sortable: true,
          value: "codigo",
          width: "25%",
        },
        {
          text: "Valor",
          align: "start",
          sortable: true,
          value: "valor",
          width: "50%",
        },
      ],

      // Options
      tablaTypeOptions: [
        { value: "CATEGORIA", text: "Categorías" },
        { value: "MARCA", text: "Marcas" },
        { value: "COLOR", text: "Colores" },
        { value: "GRUPO", text: "Grupos" },
        { value: "TALLA", text: "Tallas" },
        { value: "FORMAS-PAGO", text: "Formas de Pago" },
        { value: "FP", text: "Formas de Pago (FP)" },
        { value: "GASTOS", text: "Gastos" },
        { value: "INGRESOS", text: "Ingresos" },
        { value: "EVENTO", text: "Eventos" },
        { value: "AJ-ENTRADA", text: "Ajustes - Entrada" },
        { value: "AJ-SALIDA", text: "Ajustes - Salida" },
        { value: "CMB", text: "Cambios" },
        { value: "CMB-CAMBIO", text: "Tipos de Cambio" },
        { value: "G-ENTRADA", text: "Garantía - Entrada" },
        { value: "G-SALIDA", text: "Garantía - Salida" },
        { value: "P-ENTRADA", text: "Promoción - Entrada" },
        { value: "TB", text: "Traslados de Bodega" },
        { value: "OC", text: "Órdenes de Compra" },
        { value: "OP", text: "Órdenes de Producción" },
        { value: "OP-IN", text: "OP - Ingreso" },
      ],
    };
  },
  computed: {
    totalConfiguraciones() {
      return this.tabla_lst.length;
    },

    tiposTabla() {
      const uniqueTables = new Set(this.tabla_lst.map((item) => item.tabla));
      return uniqueTables.size;
    },

    tiposPos() {
      const uniquePos = new Set(this.tabla_lst.map((item) => item.id_pos));
      return uniquePos.size;
    },

    tablaOptions() {
      const options = [...new Set(this.tabla_lst.map((item) => item.tabla))];
      return options.map((tabla) => ({ value: tabla, text: tabla }));
    },

    idPosOptions() {
      const options = [...new Set(this.tabla_lst.map((item) => item.id_pos))];
      return options.map((pos) => ({ value: pos, text: pos }));
    },

    totalCategoriasActivas() {
      return this.tabla_lst.filter((item) => item.tabla === "CATEGORIA").length;
    },

    tipoPrincipal() {
      const tableCounts = {};
      this.tabla_lst.forEach((item) => {
        tableCounts[item.tabla] = (tableCounts[item.tabla] || 0) + 1;
      });

      const sortedTables = Object.entries(tableCounts).sort((a, b) => b[1] - a[1]);
      return sortedTables[0] ? sortedTables[0][0] : "N/A";
    },

    configuracionesPrincipales() {
      if (!this.tipoPrincipal || this.tipoPrincipal === "N/A") return 0;
      return this.tabla_lst.filter((item) => item.tabla === this.tipoPrincipal).length;
    },

    completitudPercentage() {
      if (this.totalConfiguraciones === 0) return 0;
      const complete = this.tabla_lst.filter(
        (item) => item.valor && item.valor.trim() !== ""
      ).length;
      return Math.round((complete / this.totalConfiguraciones) * 100);
    },

    sinValor() {
      return this.tabla_lst.filter((item) => !item.valor || item.valor.trim() === "")
        .length;
    },

    filteredConfiguraciones() {
      let filtered = [...this.tabla_lst];

      if (this.filterTabla) {
        filtered = filtered.filter((item) => item.tabla === this.filterTabla);
      }

      if (this.filterIdPos) {
        filtered = filtered.filter((item) => item.id_pos === this.filterIdPos);
      }

      return filtered.map((item) => ({
        ...item,
        id: `${item.id_pos}-${item.tabla}-${item.codigo}`,
      }));
    },
  },
  mounted() {
    if (this.$route && this.$route.query && this.$route.query.tabla) {
      this.filterTabla = this.$route.query.tabla;
    }
    if (this.$route && this.$route.query && this.$route.query.reason === "no_categories") {
      this.$nextTick(() => {
        if (this.snackbar) {
          this.snackbar.text =
            "Configure al menos una categoría para empezar a operar la Registradora";
          this.snackbar.color = "warning";
          this.snackbar.show = true;
        }
      });
    }
    this.getTablas();
  },
  methods: {
    async validateDuplicate() {
      if (!this.configuracion.tabla || !this.configuracion.codigo) return true;

      const qry = {
        store: window.profile.store_id,
        table: "tablas",
        filters: [
          {
            field: "id_pos",
            operator: "=",
            value: window.profile.store_id.substring(0, 6),
          },
          { field: "tabla", operator: "=", value: this.configuracion.tabla },
          { field: "codigo", operator: "=", value: this.configuracion.codigo },
        ],
      };

      return new Promise((resolve) => {
        webserver(
          "get_dataTable",
          qry,
          (data) => {
            if (data && data.length > 0 && !this.editingItem) {
              this.showMessage("Ya existe una configuración con este código", "warning");
              resolve(false);
            } else {
              resolve(true);
            }
          },
          () => {
            resolve(true); // En caso de error, permitir continuar
          }
        );
      });
    },
    // Mostrar mensajes
    showMessage(text, color = "info", timeout = 3000) {
      this.snackbar = {
        show: true,
        text,
        color,
        timeout,
      };
    },

    // Cargar datos
    getTablas() {
      this.loading_status = true;
      const qry = {
        store: window.profile.store_id, // Usar profile en lugar de store
        table: "tablas",
        filters: [
          {
            field: "id_pos",
            operator: "=",
            value: window.profile.store_id.substring(0, 6),
          },
        ],
      };

      webserver(
        "get_dataTable",
        qry,
        (data) => {
          this.loading_status = false;
          if (data) {
            this.tabla_lst = data;
            this.showMessage(`${data.length} configuraciones cargadas`, "success");
          }
        },
        (error) => {
          this.loading_status = false;
          this.showMessage("Error al cargar configuraciones: " + error, "error");
        }
      );
    },

    resetForm() {
      this.configuracion = {
        id_pos: window.profile.store_id.substring(0, 6),
        tabla: "",
        codigo: "",
        valor: "",
        codigo2: null,
      };
      this.editingItem = false;
      this.valid = false;

      console.log("Formulario reseteado:", this.configuracion);

      // Resetear validación del formulario
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.resetValidation();
        }
      });
    },

    // Guardar configuración
    saveConfiguracion() {
      console.log("=== GUARDANDO CONFIGURACIÓN ===");
      console.log("Datos:", this.configuracion);
      console.log("Es edición?", this.editingItem);

      if (!this.$refs.form.validate()) {
        this.showMessage("Por favor complete todos los campos requeridos", "warning");
        return;
      }

      this.saving = true;

      // Asegurar que id_pos tenga el valor correcto
      this.configuracion.id_pos = window.profile.store_id.substring(0, 6);

      const qry = {
        store: window.profile.store_id,
        table: "tablas",
        data: [{ ...this.configuracion }], // Crear copia
      };

      console.log("Query a enviar:", qry);

      webserver(
        "put_table",
        qry,
        (data) => {
          this.saving = false;
          console.log("=== RESPUESTA EXITOSA ===");
          console.log("Respuesta del servidor:", data);

          this.showMessage(
            this.editingItem
              ? "Configuración actualizada exitosamente"
              : "Configuración creada exitosamente",
            "success"
          );

          // Recargar la lista de configuraciones
          this.getTablas();

          // Cerrar el diálogo después de un breve delay
          setTimeout(() => {
            this.closeDialog();
          }, 500);
        },
        (error) => {
          this.saving = false;
          console.error("=== ERROR AL GUARDAR ===");
          console.error("Error:", error);
          this.showMessage("Error al guardar: " + error, "error");
        }
      );
    },

    // Confirmar eliminación
    confirmDelete(item) {
      this.selectedForDelete = item;
      this.deleteDialog = true;
    },

    // Cancelar eliminación
    cancelDelete() {
      this.deleteDialog = false;
      this.selectedForDelete = null;
    },

    // Editar item
    editItem(item) {
      console.log("Editando item desde fila:", item); // Para debug

      // El item viene desde el click:row event
      this.configuracion = {
        id_pos: item.id_pos,
        tabla: item.tabla,
        codigo: item.codigo,
        codigo2: item.codigo2 || null,
        valor: item.valor || "",
      };

      // Marcar como edición
      this.editingItem = true;
      this.dialog = true;

      console.log("Configuración cargada para edición:", this.configuracion);

      // Resetear validación en el próximo tick
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.resetValidation();
        }
      });
    },

    // Nuevo formulario
    newForm() {
      console.log("Creando nueva configuración");
      this.resetForm();
      this.dialog = true;
    },

    // Cerrar diálogo
    closeDialog() {
      console.log("Cerrando diálogo");
      this.dialog = false;
      this.resetForm();
    },

    // Eliminar item
    deleteItem() {
      if (!this.selectedForDelete) return;

      this.deleting = true;
      const qry = {
        store: window.profile.store_id,
        table: "tablas",
        filters: [
          { field: "id_pos", operator: "=", value: this.selectedForDelete.id_pos },
          { field: "tabla", operator: "=", value: this.selectedForDelete.tabla },
          { field: "codigo", operator: "=", value: this.selectedForDelete.codigo },
        ],
      };

      webserver(
        "delete_from_table",
        qry,
        () => {
          this.deleting = false;
          this.deleteDialog = false;
          this.selectedForDelete = null;
          this.showMessage("Configuración eliminada exitosamente", "success");

          // Recargar la lista
          this.getTablas();
        },
        (error) => {
          this.deleting = false;
          this.showMessage("Error al eliminar: " + error, "error");
        }
      );
    },

    // Obtener color para chip de tabla
    getTablaColor(tabla) {
      const colors = {
        CATEGORIA: "purple lighten-4",
        MARCA: "blue lighten-4",
        COLOR: "pink lighten-4",
        GRUPO: "teal lighten-4",
        TALLA: "orange lighten-4",
        "FORMAS-PAGO": "green lighten-4",
        FP: "green lighten-4",
        GASTOS: "red lighten-4",
        INGRESOS: "blue lighten-4",
        EVENTO: "amber lighten-4",
        "AJ-ENTRADA": "cyan lighten-4",
        "AJ-SALIDA": "indigo lighten-4",
      };
      return colors[tabla] || "grey lighten-4";
    },

    // Obtener color de texto para chip
    getTablaTextColor(tabla) {
      const darkText = ["COLOR", "TALLA", "EVENTO"];
      return darkText.includes(tabla) ? "black" : "black";
    },

    // Obtener icono para tabla
    getTablaIcon(tabla) {
      const icons = {
        CATEGORIA: "mdi-shape",
        MARCA: "mdi-tag",
        COLOR: "mdi-palette",
        GRUPO: "mdi-group",
        TALLA: "mdi-ruler",
        "FORMAS-PAGO": "mdi-cash",
        FP: "mdi-cash-multiple",
        GASTOS: "mdi-cash-minus",
        INGRESOS: "mdi-cash-plus",
        EVENTO: "mdi-calendar",
        "AJ-ENTRADA": "mdi-arrow-down",
        "AJ-SALIDA": "mdi-arrow-up",
      };
      return icons[tabla] || "mdi-cog";
    },

    // Convertir a mayúsculas
    upperCase(value) {
      this.configuracion.codigo = value.toUpperCase();
    },

    // Exportar a Excel
    exportToExcel() {
      const csv = this.generateCSV();
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `configuraciones_${new Date().toISOString().split("T")[0]}.csv`;
      link.click();
      this.showMessage("Archivo exportado exitosamente", "success");
    },

    // Generar CSV
    generateCSV() {
      const headers = ["ID POS", "Tabla", "Código", "Valor", "Código 2"];

      const rows = this.tabla_lst.map((item) => [
        item.id_pos || "",
        item.tabla || "",
        item.codigo || "",
        item.valor || "",
        item.codigo2 || "",
      ]);

      const csv = [headers, ...rows]
        .map((row) =>
          row.map((cell) => `"${cell.toString().replace(/"/g, '""')}"`).join(",")
        )
        .join("\n");

      return csv;
    },
  },
  watch: {
    // Watcher para resetear formulario cuando se abre el diálogo
    dialog(newVal) {
      if (newVal && !this.editingItem) {
        this.resetForm();
      }
    },
  },
};
</script>

<style scoped>
/* KPI Cards */
.kpi-card {
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
}

.kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
}

.kpi-card .v-card__text {
  display: flex;
  align-items: center;
  gap: 16px;
}

.kpi-icon {
  background-color: #f5f5f5;
  border-radius: 12px;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.kpi-icon .v-icon {
  font-size: 28px !important;
}

.kpi-content {
  flex: 1;
  min-width: 0;
}

.kpi-label {
  font-size: 0.875rem;
  color: #666;
  font-weight: 500;
  margin-bottom: 4px;
}

.kpi-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: #333;
  line-height: 1.2;
}

.kpi-subtitle {
  font-size: 0.75rem;
  color: #999;
  margin-top: 4px;
  display: flex;
  align-items: center;
}

/* Main Card */
.main-card {
  border: 1px solid #e0e0e0;
}

.minimal-toolbar {
  border-bottom: 1px solid #e0e0e0;
  padding: 16px 24px;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
}

.dialog-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #333;
}

/* Search Field */
.search-field >>> .v-input__control {
  background-color: white;
}

/* Data Table */
.minimal-table {
  border: none !important;
}

.minimal-table >>> .v-data-table__wrapper {
  border: none;
}

.minimal-table >>> thead tr th {
  background-color: #fafafa !important;
  font-weight: 600 !important;
  color: #666 !important;
  border-bottom: 2px solid #e0e0e0 !important;
  font-size: 0.875rem !important;
  padding: 16px !important;
}

.minimal-table >>> tbody tr {
  border-bottom: 1px solid #f0f0f0 !important;
  cursor: pointer;
  transition: background-color 0.2s;
}

.minimal-table >>> tbody tr:hover {
  background-color: #f5f5f5 !important;
}

.minimal-table >>> tbody tr td {
  padding: 16px !important;
  font-size: 0.875rem !important;
}

/* Custom cells */
.code-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.value-info {
  display: flex;
  align-items: center;
}

/* Loading */
.minimal-table >>> .v-data-table__progress {
  height: 2px !important;
}

/* Responsive */
@media (max-width: 960px) {
  .kpi-value {
    font-size: 1.5rem;
  }

  .section-title {
    font-size: 1.25rem;
  }

  .kpi-card .v-card__text {
    flex-direction: column;
    text-align: center;
  }

  .minimal-toolbar {
    flex-wrap: wrap;
    gap: 16px;
  }
  /* Filas clickeables */
  .clickable-rows >>> tbody tr {
    cursor: pointer !important;
    transition: background-color 0.2s ease;
  }

  .clickable-rows >>> tbody tr:hover {
    background-color: #f5f5f5 !important;
  }

  /* Indicador visual de que las filas son clickeables */
  .clickable-rows >>> tbody tr:hover td {
    background-color: transparent !important;
  }

  /* Opcional: agregar un indicador visual */
  .minimal-table >>> tbody tr td:last-child::after {
    content: "";
    display: inline-block;
    width: 4px;
    height: 4px;
    background-color: #1976d2;
    border-radius: 50%;
    margin-left: 8px;
    opacity: 0;
    transition: opacity 0.2s;
  }

  .minimal-table >>> tbody tr:hover td:last-child::after {
    opacity: 1;
  }
}
</style>
